
import { Fragment, useState, useEffect } from 'react';
import { User, UserRole, CreateUser, UpdateUser } from '@/types';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select, SelectOption } from '@/components/ui/Select';
import { formatFullName } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUser | UpdateUser) => Promise<void>;
  user?: User | null; // null for create, User for edit
  isLoading?: boolean;
  /** When false, hide video/audio permission checkboxes and omit them on save. */
  responsesEnabled?: boolean;
}

const roleOptions: SelectOption[] = [
  { value: UserRole.CONGREGANT, label: 'Congregant' },
  { value: UserRole.PRAYER_PARTNER, label: 'Prayer Partner' },
  { value: UserRole.MODERATOR, label: 'Moderator' },
  { value: UserRole.PRODUCER, label: 'Producer' },
  { value: UserRole.ADMIN, label: 'Admin' },
];

export function UserFormModal({
  isOpen,
  onClose,
  onSubmit,
  user,
  isLoading = false,
  responsesEnabled = true,
}: UserFormModalProps) {
  const isEdit = !!user;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: UserRole.MODERATOR,
    canRespondVideo: false,
    canRespondAudio: false,
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email,
          role: user.role,
          canRespondVideo: user.canRespondVideo ?? false,
          canRespondAudio: user.canRespondAudio ?? false,
          password: '',
          confirmPassword: '',
        });
      } else {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          role: UserRole.MODERATOR,
          canRespondVideo: false,
          canRespondAudio: false,
          password: '',
          confirmPassword: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, user]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (isEdit) {
      const updateData: UpdateUser = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        ...(responsesEnabled
          ? {
              canRespondVideo: formData.canRespondVideo,
              canRespondAudio: formData.canRespondAudio,
            }
          : {}),
      };
      await onSubmit(updateData);
    } else {
      const createData: CreateUser = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        ...(responsesEnabled
          ? {
              canRespondVideo: formData.canRespondVideo,
              canRespondAudio: formData.canRespondAudio,
            }
          : {}),
        password: formData.password,
      };
      await onSubmit(createData);
    }
  };

  if (!isOpen) return null;

  return (
    <Fragment>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            'bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full',
            'animate-fade-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-form-title"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2
              id="user-form-title"
              className="text-lg font-semibold text-slate-900 dark:text-white"
            >
              {isEdit ? 'Edit User' : 'Create New User'}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {isEdit
                ? 'Update user information and role.'
                : 'Add a new staff member to the system.'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <Input
                label="First name"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                error={errors.firstName}
                placeholder="John"
                required
              />
              <Input
                label="Last name"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                error={errors.lastName}
                placeholder="Doe"
                required
              />

              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                error={errors.email}
                placeholder="john@example.com"
                required
              />

              <Select
                label="Role"
                options={roleOptions}
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value as UserRole;
                  setFormData({
                    ...formData,
                    role: newRole,
                    canRespondVideo: newRole === UserRole.CONGREGANT ? false : formData.canRespondVideo,
                    canRespondAudio: newRole === UserRole.CONGREGANT ? false : formData.canRespondAudio,
                  });
                }}
              />

              {responsesEnabled && formData.role !== UserRole.CONGREGANT && (
                <>
                  <Checkbox
                    label="Can respond with video"
                    description="Allow this user to record and send video responses to prayers"
                    checked={formData.canRespondVideo}
                    onChange={(e) =>
                      setFormData({ ...formData, canRespondVideo: e.target.checked })
                    }
                  />
                  <Checkbox
                    label="Can respond with audio"
                    description="Allow this user to record and send audio-only responses to prayers"
                    checked={formData.canRespondAudio}
                    onChange={(e) =>
                      setFormData({ ...formData, canRespondAudio: e.target.checked })
                    }
                  />
                </>
              )}

              {!isEdit && (
                <>
                  <Input
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    error={errors.password}
                    placeholder="Minimum 8 characters"
                    required
                  />

                  <Input
                    label="Confirm Password"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        confirmPassword: e.target.value,
                      })
                    }
                    error={errors.confirmPassword}
                    placeholder="Re-enter password"
                    required
                  />
                </>
              )}
            </div>

            {/* Footer */}
<div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                {isEdit ? 'Save Changes' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
}

// Password Reset Modal
export interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newPassword: string) => Promise<void>;
  user: User | null;
  isLoading?: boolean;
}

export function PasswordResetModal({
  isOpen,
  onClose,
  onSubmit,
  user,
  isLoading = false,
}: PasswordResetModalProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirmPassword('');
      setErrors({});
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(password);
  };

  if (!isOpen || !user) return null;

  return (
    <Fragment>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            'bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full',
            'animate-fade-in'
          )}
          role="dialog"
          aria-modal="true"
        >
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Reset Password
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Set a new password for{' '}
              <span className="font-medium">{formatFullName(user.firstName, user.lastName) || user.email}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <Input
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                placeholder="Minimum 8 characters"
                required
              />

              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={errors.confirmPassword}
                placeholder="Re-enter password"
                required
              />
            </div>

            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                Reset Password
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
}
